// ── Mock Data for ThriveLonger.ai ──

export const COLORS = {
  background: '#F9F6F0',
  surface: '#FFFFFF',
  surfaceSecondary: '#F0EAD6',
  primary: '#6B8E23',
  primaryActive: '#55711C',
  secondary: '#4682B4',
  accentWarm: '#D2B48C',
  textPrimary: '#2F3E46',
  textSecondary: '#5C6B73',
  border: '#E2DAC8',
  error: '#B22222',
  success: '#3CB371',
  tierFree: '#6B8E23',
  tierAnalyst: '#4682B4',
  tierOptimizer: '#DAA520',
  tierElite: '#8B008B',
};

export const STEPS = [
  { num: '01', icon: 'ClipboardList', title: 'Complete Your Health Survey', desc: 'Answer our comprehensive 35+ factor health assessment covering diet, exercise, sleep, mental wellness, and more.' },
  { num: '02', icon: 'BarChart3', title: 'Get Your Health Score', desc: 'Receive a personalized 9-category health score with longevity prediction and actionable breakdown.' },
  { num: '03', icon: 'Rocket', title: 'Optimize with AI', desc: 'Use AI coaching, what-if analysis, goal tracking, and wearable integration to improve your healthspan.' },
];

export const FEATURES = [
  { icon: 'BarChart3', title: 'Health Analytics', desc: 'Track & optimize your vital metrics with a 9-factor scoring engine that predicts your longevity.' },
  { icon: 'MessageCircle', title: 'AI Health Coach', desc: 'Personalized coaching powered by GPT-5.2 that understands your unique health profile.' },
  { icon: 'Dumbbell', title: 'Goal Tracking', desc: 'Set and achieve longevity goals with templates, calendar tracking, and severity monitoring.' },
  { icon: 'Watch', title: 'Wearable Integration', desc: 'Sync your health wearables and see real-time metrics like heart rate, HRV, sleep, and more.' },
  { icon: 'FlaskConical', title: 'What-If Analysis', desc: 'Project how lifestyle changes impact your health score & predicted longevity in real-time.' },
  { icon: 'Trophy', title: 'Community Challenges', desc: 'Compete with others on step counts, sleep, and mindfulness in weekly health challenges.' },
];

export const TESTIMONIALS = [
  { name: 'Sarah Mitchell', age: 42, tier: 'Optimizer', init: 'SM', color: '#DAA520', score: '+22', quote: 'ThriveLonger.ai transformed my approach to health. My score went from 62 to 84 in just 3 months. The AI coaching identified sleep issues I never noticed.', stars: 5 },
  { name: 'James Kwon', age: 55, tier: 'Elite', init: 'JK', color: '#8B008B', score: '+18', quote: 'The AI coach gave me insights my doctor never mentioned. The what-if analysis showed me exactly how intermittent fasting would impact my longevity.', stars: 5 },
  { name: 'Maria Luciano', age: 38, tier: 'Analyst', init: 'ML', color: '#4682B4', score: '+15', quote: 'I love the wearable integration. Seeing all my data in one place with actionable tips is incredible. My exercise score jumped 30 points!', stars: 5 },
];

export const TIERS = [
  { name: 'Explorer', price: 'Free', color: COLORS.tierFree, icon: 'Compass', features: ['White papers & research', 'Meta-analyses library', 'Resource links', 'Self-help guides'] },
  { name: 'Analyst', price: '$29.99', sub: '/mo', color: COLORS.tierAnalyst, icon: 'BarChart2', features: ['All Explorer benefits', 'Personalized health scoring', 'What-if analysis', 'Predictive metrics'] },
  { name: 'Optimizer', price: '$79.99', sub: '/mo', color: COLORS.tierOptimizer, icon: 'TrendingUp', popular: true, features: ['All Analyst benefits', 'SMS & email alerts', 'Weekly health summaries', 'Intervention plans'] },
  { name: 'Elite', price: '$199.99', sub: '/mo', color: COLORS.tierElite, icon: 'Diamond', features: ['All Optimizer benefits', 'Weekly AI coaching', 'Monthly live consultation', 'Premium partner discounts'] },
];

export const FAQS = [
  { q: 'What is ThriveLonger.ai?', a: 'ThriveLonger.ai is a comprehensive health and longevity optimization platform that combines research from NIA, Stanford, and Lifespan.io with AI-powered coaching to help you live longer and healthier.' },
  { q: 'How does the health scoring work?', a: 'Our scoring engine analyzes 9 key SHIELDED categories — Sleep, Headspace (stress relief), Interaction (loved ones), Exercise, Limit Exposure (toxins/EMF/alcohol/smoking), Diet, Environment, Discovery (lifelong learning & purpose), plus Genetics & Biomarkers — to generate a 0-100 score with a longevity estimate.' },
  { q: 'Is my health data secure?', a: 'Yes. We operate following HIPAA guidelines to protect your information, with AES-256 encryption at rest and TLS 1.3 in transit. Your data is never sold to third parties. (Hover the "HIPAA guidelines" label anywhere on the site for the full explainer.)' },
  { q: "What's included in the free trial?", a: 'You get 7 days of our Analyst tier (normally $29.99/mo), which includes personalized health scoring, what-if analysis, predictive metrics, and full survey access.' },
  { q: 'How does the AI coaching work?', a: 'Every tier includes some AI coaching with a monthly credit allowance — Free starts with 5 credits, Analyst 25, Optimizer 100, Elite unlimited. Buy additional credit packs anytime. Coaching is powered by GPT-5.2 and understands your health profile, scores, and goals. Coming soon: Elite members will be able to match with a live human expert in their chosen SHIELDED category for an additional per-session fee paid directly to the expert.' },
  { q: 'Can I cancel anytime?', a: 'Yes! You can cancel your subscription at any time with no penalties. Your access continues through the current billing period.' },
];

export const STATS = [
  { num: '100+', label: 'Health Factors' },
  { num: '7-Day', label: 'Free Trial' },
  { num: 'GPT-5.2', label: 'AI Coaching' },
  { num: 'HIPAA', label: 'Following Guidelines' },
];

export const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Research', href: '/research' },
  { label: 'Your Community', href: '/community' },
  { label: 'Blog', href: '/blog' },
  { label: 'Store', href: '/dashboard/store' },
];

export const MOCK_USER = {
  name: 'Alex Johnson',
  email: 'alex@thrivelonger.ai',
  membership_tier: 4,
  trial_active: true,
  trial_end: '2025-08-01',
  survey_completed: true,
  role: 'user',
};

export const TIER_NAMES = ['Free', 'Explorer', 'Analyst', 'Optimizer', 'Elite'];
export const TIER_COLORS = ['#999', COLORS.tierFree, COLORS.tierAnalyst, COLORS.tierOptimizer, COLORS.tierElite];

export const MOCK_GOALS = [
  { id: '1', title: 'Improve Cardio Endurance', description: 'Run 5K three times per week', category: 'diet_exercise', severity: 3, status: 'active' },
  { id: '2', title: 'Better Sleep Hygiene', description: 'Achieve 8 hours of quality sleep nightly', category: 'general', severity: 5, status: 'active' },
  { id: '3', title: 'Reduce Sugar Intake', description: 'Limit added sugar to under 25g daily', category: 'diet_exercise', severity: 4, status: 'in_progress' },
];

export const MOCK_CONTENT = [
  { id: '1', title: 'The Science of Sleep & Longevity', body: 'Research from Stanford\'s Center on Longevity shows that consistent 7-9 hours of sleep can add up to 5 years to your lifespan...', tier_required: 0 },
  { id: '2', title: 'Mediterranean Diet & Healthspan', body: 'A comprehensive meta-analysis of over 12,000 participants reveals the Mediterranean diet reduces all-cause mortality by 23%...', tier_required: 1 },
  { id: '3', title: 'Exercise Intensity & Telomere Length', body: 'NIA-funded research demonstrates that high-intensity interval training (HIIT) preserves telomere length more effectively...', tier_required: 2 },
];

export const SCORE_META = {
  exercise: { label: 'Exercise', icon: 'Dumbbell', color: '#4CAF50' },
  sleep: { label: 'Sleep', icon: 'Moon', color: '#5C6BC0' },
  health_perception: { label: 'Diet', icon: 'Apple', color: '#EF5350' },
  mental_wellness: { label: 'Headspace', icon: 'Smile', color: '#FF9800' },
  substance: { label: 'Limit Exposure', icon: 'ShieldOff', color: '#8D6E63' },
  chronic_health: { label: 'Genetics & Biomarkers', icon: 'Dna', color: '#26A69A' },
  social_engagement: { label: 'Interaction', icon: 'Users', color: '#42A5F5' },
  body_composition: { label: 'Environment', icon: 'Leaf', color: '#7E57C2' },
  mobility: { label: 'Discovery', icon: 'Compass', color: '#66BB6A' },
};

// SHIELDED — the 8 epigenetic ADL pillars + Genetics & Biomarkers (the 9th
// foundational category) make up the program's scoring framework.
export const SHIELDED_PILLARS = [
  { letter: 'S', label: 'Sleep', desc: '7-9 hours of restorative sleep nightly.' },
  { letter: 'H', label: 'Headspace', desc: 'Mindfulness, stress relief, mental wellness.' },
  { letter: 'I', label: 'Interaction', desc: 'Quality time with loved ones; strong social ties.' },
  { letter: 'E', label: 'Exercise', desc: 'Movement, strength, cardio, VILPA bursts.' },
  { letter: 'L', label: 'Limit Exposure', desc: 'Avoid toxins, EMFs, alcohol, smoking.' },
  { letter: 'D', label: 'Diet', desc: 'Whole foods, hydration, Mediterranean patterns.' },
  { letter: 'E', label: 'Environment', desc: 'Sunlight, clean air, nature, ergonomic spaces.' },
  { letter: 'D', label: 'Discovery', desc: 'Lifelong learning, purpose, curiosity.' },
];

export const MOCK_SCORES = {
  overall: 78,
  estimated_longevity: 89,
  years_remaining: 48,
  exercise: 82,
  sleep: 71,
  health_perception: 80,
  mental_wellness: 75,
  substance: 90,
  chronic_health: 85,
  social_engagement: 68,
  body_composition: 72,
  mobility: 79,
};

export const MOCK_HISTORY = [
  { date: '2025-06-01', overall: 65, scores: { exercise: 60, sleep: 58, health_perception: 70, mental_wellness: 65, substance: 88, chronic_health: 80, social_engagement: 55, body_composition: 62, mobility: 70 } },
  { date: '2025-06-08', overall: 68, scores: { exercise: 65, sleep: 62, health_perception: 72, mental_wellness: 68, substance: 89, chronic_health: 82, social_engagement: 58, body_composition: 65, mobility: 72 } },
  { date: '2025-06-15', overall: 70, scores: { exercise: 70, sleep: 65, health_perception: 75, mental_wellness: 70, substance: 89, chronic_health: 83, social_engagement: 60, body_composition: 67, mobility: 74 } },
  { date: '2025-06-22', overall: 73, scores: { exercise: 75, sleep: 68, health_perception: 77, mental_wellness: 72, substance: 90, chronic_health: 84, social_engagement: 63, body_composition: 69, mobility: 76 } },
  { date: '2025-06-29', overall: 76, scores: { exercise: 80, sleep: 70, health_perception: 79, mental_wellness: 74, substance: 90, chronic_health: 85, social_engagement: 66, body_composition: 71, mobility: 78 } },
  { date: '2025-07-06', overall: 78, scores: { exercise: 82, sleep: 71, health_perception: 80, mental_wellness: 75, substance: 90, chronic_health: 85, social_engagement: 68, body_composition: 72, mobility: 79 } },
];

export const GOAL_TEMPLATES = [
  { title: 'Walk 10,000 Steps Daily', description: 'Build a consistent walking habit for cardiovascular health', category: 'diet_exercise' },
  { title: 'Meditate for 10 Minutes', description: 'Daily meditation practice for mental wellness', category: 'spiritual' },
  { title: 'Track Macros', description: 'Log daily macronutrient intake for optimal nutrition', category: 'diet_exercise' },
  { title: 'Read Health Research', description: 'Read one longevity research paper per week', category: 'technology' },
  { title: 'Social Check-in', description: 'Connect with a friend or family member daily', category: 'general' },
];

export const MOCK_MESSAGES = [
  { role: 'assistant', content: 'Hello! I\'m your ThriveLonger.ai Health Coach. How can I help you optimize your health today?' },
];

export const CHAT_SUGGESTIONS = [
  'How can I improve my sleep?',
  'Best supplements for longevity?',
  'Create a weekly exercise plan',
];

export const STORE_CATEGORIES = [
  { slug: 'health-devices', name: 'Health Devices', icon: 'Activity' },
  { slug: 'consultations', name: 'Consultations', icon: 'UserCheck' },
  { slug: 'seminars', name: 'Seminars', icon: 'GraduationCap' },
  { slug: 'clothing', name: 'Clothing', icon: 'Shirt' },
  { slug: 'environmental', name: 'Environmental', icon: 'Leaf' },
];

export const STORE_PRODUCTS = [
  { id: 'p1', name: 'Smart Blood Pressure Monitor', description: 'FDA-cleared wireless BP monitor with app integration', price: 89.99, category: 'health-devices', icon: 'Heart', featured: true, rating: 4.8, reviewCount: 124 },
  { id: 'p2', name: 'Sleep Optimization Kit', description: 'Blue light glasses, magnesium supplement & sleep mask bundle', price: 49.99, category: 'health-devices', icon: 'Moon', featured: true, rating: 4.6, reviewCount: 89 },
  { id: 'p3', name: '1-on-1 Nutrition Consultation', description: '60-minute session with a certified nutritionist', price: 149.99, category: 'consultations', icon: 'UserCheck', featured: false, rating: 4.9, reviewCount: 45 },
  { id: 'p4', name: 'Longevity Science Masterclass', description: '8-week online course on the science of aging', price: 299.99, category: 'seminars', icon: 'GraduationCap', featured: true, rating: 4.7, reviewCount: 67 },
  { id: 'p5', name: 'Organic Performance Tee', description: 'Moisture-wicking organic cotton athletic shirt', price: 39.99, category: 'clothing', icon: 'Shirt', featured: false, rating: 4.4, reviewCount: 32 },
  { id: 'p6', name: 'Air Quality Monitor', description: 'Real-time indoor air quality tracking with health alerts', price: 129.99, category: 'environmental', icon: 'Wind', featured: false, rating: 4.5, reviewCount: 56 },
  { id: 'p7', name: 'Advanced HRV Tracker', description: 'Clinical-grade heart rate variability monitor', price: 199.99, category: 'health-devices', icon: 'Activity', featured: true, rating: 4.7, reviewCount: 91 },
  { id: 'p8', name: 'Ergonomic Standing Desk Mat', description: 'Anti-fatigue mat for standing desk users', price: 69.99, category: 'environmental', icon: 'Footprints', featured: false, rating: 4.3, reviewCount: 28 },
];

export const MOCK_CART = [];

export const MOCK_SURVEY = {
  age: 41,
  weight: 175,
  height: 70,
  exercise_level: 5,
  health_perception: 5,
  hours_sleep: 7,
};

export const CATEGORIES = ['general', 'diet_exercise', 'supplements', 'spiritual', 'technology', 'genetic', 'talk_media'];
